import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@cargomatic/ui';
import SessionStore from 'stores/SessionStore';
import { isNumber } from 'lodash';
import {
  getUnitCalculationView,
  reconcileCalcMethod,
  UNIT_CALC_AVAILABLE,
  SHIPPER_CARRIER,
} from 'utils/accessorials/unitCalculation';
import Alert from '../../ui/Alert';
import ErrorAlert from '../../ui/ErrorAlert';
import UnitCalculationSection from './components/UnitCalculationSection';

import {
  createAccessorial,
  updateAccessorial,
} from '../../../utils/AccessorialWebAPIUtils';

const ICON_COUNT = 36;

const shipmentTypes = [
  { localizationSlug: 'DRAYAGE', value: 'drayage' },
  { localizationSlug: 'SHORTHAUL', value: 'shorthaul' },
];

const rateTypes = [
  { localizationSlug: 'FLAT', value: 'flat' },
  { localizationSlug: 'HOURLY', value: 'hourly' },
  { localizationSlug: 'DAILY', value: 'daily' },
];

const unitTypes = [
  { localizationSlug: 'DECIMAL', value: 'decimal' },
  { localizationSlug: 'NUMBER', value: 'number' },
];

const ORDER_CREATION_DATE = 'ORDER_CREATION_DATE';
const PICKUP_STOP = 'PICKUP_STOP';
const DELIVERY_STOP = 'DELIVERY_STOP';
const DELIVERY_EMPTY = 'DELIVERY_EMPTY';

export const anchorEventOptions = [
  {
    localizationSlug: ORDER_CREATION_DATE,
    name: 'Order Creation Date',
    value: ORDER_CREATION_DATE,
  },
  {
    localizationSlug: PICKUP_STOP,
    name: 'Pickup Stop',
    value: PICKUP_STOP,
  },
  {
    localizationSlug: DELIVERY_STOP,
    name: 'Delivery Stop',
    value: DELIVERY_STOP,
  },
  {
    localizationSlug: DELIVERY_EMPTY,
    name: 'Delivery Empty',
    value: DELIVERY_EMPTY,
  },
];

function mapAccessorial(accessorial = {}) {
  return {
    name: '',
    shipment_type: 'drayage',
    begin_date_required: false,
    end_date_required: false,
    location_required: false,
    description_required: false,
    rate_type: 'flat',
    unit_type: 'decimal',
    admin_only: false,
    auto_calculate_units: false,
    calc_method: null,
    applies_to: SHIPPER_CARRIER,
    decimal_places: 2,
    minimum_units: '',
    ...accessorial,
  };
}

// Keeps calc_method valid for the current Rate/Unit Type and forces
// auto_calculate_units off whenever the section isn't actually available, so we
// never save an invisible/invalid auto-calculate config.
function reconcileUnitCalculationState(accessorial) {
  const reconciled = reconcileCalcMethod(accessorial);
  if (getUnitCalculationView(reconciled).mode !== UNIT_CALC_AVAILABLE) {
    return { ...reconciled, auto_calculate_units: false };
  }
  return reconciled;
}

class AccessorialForm extends Component {
  constructor(props) {
    const { accessorial } = props;
    super(props);
    this.state = {
      accessorial: reconcileUnitCalculationState(mapAccessorial(accessorial)),
      success: false,
      saving: false,
      errors: {},
    };
  }

  handleSelectIcon = (icon_slug) => () => {
    this.setState((prevState) => ({
      accessorial: { ...prevState.accessorial, icon_slug },
    }));
  };

  handleFieldChange = (field, type) => (value) => {
    let changedValue = value;
    if (type === 'number' && isNumber(value)) {
      changedValue = Number(value);
    }
    this.setState((prevState) => ({
      accessorial: { ...prevState.accessorial, [field]: changedValue },
    }));
  };

  handleInputChange = (field, type) => (e) => {
    this.handleFieldChange(
      field,
      type,
    )(
      type === 'number' && e.target.value !== ''
        ? Number(e.target.value)
        : e.target.value,
    );
  };

  handleSwitchChange = (field) => (checked) => {
    this.setState((prevState) => {
      const accessorial = { ...prevState.accessorial, [field]: checked };
      if (field === 'begin_date_required' || field === 'end_date_required') {
        return { accessorial: reconcileUnitCalculationState(accessorial) };
      }
      return { accessorial };
    });
  };

  handleSelectChange = (field) => (value) => {
    this.setState((prevState) => {
      const accessorial = { ...prevState.accessorial, [field]: value };
      if (field === 'rate_type' || field === 'unit_type') {
        return { accessorial: reconcileUnitCalculationState(accessorial) };
      }
      return { accessorial };
    });
  };

  handleSave = () => {
    const { t } = this.props;
    this.setState(
      (prevState) => {
        const errors = this.validate(prevState.accessorial);
        const saving = Object.keys(errors).length === 0;
        return { errors, saving };
      },
      async () => {
        if (!this.state.saving) return;

        const saveFn = this.state.accessorial._id
          ? updateAccessorial
          : createAccessorial;

        try {
          const result = await saveFn({
            ...this.state.accessorial,
          });

          const {
            entity: { accessorial },
          } = result;

          this.setState(
            { saving: false, errors: {}, accessorial, success: true },
            () => {
              setTimeout(() => {
                this.props.onSaveSuccess(this.state.accessorial);
              }, 400);
            },
          );
        } catch (e) {
          console.error(e);
          let error;
          if (e.entity && e.entity.error.includes('duplicate key error')) {
            if (e.entity.error.includes('charge_code')) {
              error = t('CHARGE_CODE_ALREADY_EXISTS');
            } else {
              error = t('ACCESSORIAL_ALREADY_EXISTS');
            }
          } else {
            error = t('PROBLEM_SAVING_ACCESSORIAL');
          }

          this.setState({
            saving: false,
            success: false,
            errors: { base: error },
          });
        }
      },
    );
  };

  validate(accessorial) {
    const { t } = this.props;
    const { name, charge_code, rate_type, unit_type } = accessorial;

    const errors = {};
    if (!name) errors.name = t('NAME_REQUIRED_ERROR');
    if (!charge_code) errors.charge_code = t('CHARGE_CODE_REQUIRED_ERROR');
    if (!rate_type) errors.rate_type = t('RATE_TYPE_REQUIRED_ERROR');
    if (!unit_type) errors.unit_type = t('UNIT_TYPE_REQUIRED_ERROR');
    return errors;
  }

  renderSuccess() {
    const { t } = this.props;
    const { success, saving } = this.state;
    if (saving || !success) return null;

    return (
      <Alert
        type="success"
        onClose={this.handleCloseSuccessAlert}
        canClose
        title={t('ACCESSORIAL_SAVED_SUCCESSFULLY')}
      />
    );
  }

  renderErrors() {
    const { t } = this.props;
    const { errors } = this.state;
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return null;

    return (
      <ErrorAlert
        title={t('FIX_FOLLOWING_ISSUES')}
        canClose
        onClose={this.handleHideErrorAlert}
      >
        <ul className="ml-0 list-reset">
          {errorKeys.map((errorKey) => (
            <li key={errorKey}>{this.state.errors[errorKey]}</li>
          ))}
        </ul>
      </ErrorAlert>
    );
  }

  renderToggleRow(id, field, labelKey) {
    const { t } = this.props;
    const checked = this.state.accessorial[field];
    return (
      <div className="flex items-center gap-3">
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={this.handleSwitchChange(field)}
        />
        <Label htmlFor={id} className="text-sm font-normal cursor-pointer">
          {t(labelKey)}
        </Label>
      </div>
    );
  }

  renderIconPicker() {
    const { t } = this.props;
    const { icon_slug } = this.state.accessorial;
    return (
      <div className="space-y-1">
        <Label className="block">{t('ICON')}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title={t('CHOOSE_ICON', 'Choose icon')}
              aria-label={t('CHOOSE_ICON', 'Choose icon')}
              className="flex items-center justify-center w-10 h-10 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <i
                className={classnames(
                  icon_slug || 'icon-1',
                  'icon-normal',
                  'mx-0',
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="grid grid-cols-7 gap-1">
              {Array(ICON_COUNT)
                .fill()
                .map((_, i) => {
                  const iconSlug = `icon-${i + 1}`;
                  const active = icon_slug === iconSlug;
                  const cellClassName = classnames(
                    'flex items-center justify-center p-2 rounded-md transition-colors cursor-pointer',
                    {
                      'bg-brand text-brand-foreground': active,
                      'text-muted-foreground hover:bg-accent hover:text-foreground':
                        !active,
                    },
                  );
                  return (
                    <button
                      type="button"
                      key={i}
                      aria-label={t('SELECT_ICON', {
                        defaultValue: 'Icon {{number}}',
                        number: i + 1,
                      })}
                      aria-pressed={active}
                      className={cellClassName}
                      onClick={this.handleSelectIcon(iconSlug)}
                    >
                      <i
                        className={classnames(iconSlug, 'icon-normal', 'mx-0')}
                      />
                    </button>
                  );
                })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const {
      accessorial: {
        name,
        charge_code,
        shipment_type,
        rate_type,
        unit_type,
        days_before_auto_decline,
        anchor_event,
      },
    } = this.state;
    const isDrayage = shipment_type === 'drayage';
    const hasFinanceAdminRole = SessionStore.hasFinanceAdminRole();

    return (
      <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {this.renderSuccess()}
          {this.renderErrors()}

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4">
            <div className="space-y-1">
              <Label htmlFor="accessorial-name">{t('NAME')}</Label>
              <Input
                id="accessorial-name"
                type="text"
                value={name || ''}
                placeholder={t('ENTER_ACCESSORIAL_NAME')}
                onChange={this.handleInputChange('name')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="accessorial-charge-code">
                {t('CHARGE_CODE')}
              </Label>
              <Input
                id="accessorial-charge-code"
                type="text"
                value={charge_code || ''}
                placeholder={t('ENTER_ACCESSORIAL_CHARGE_CODE')}
                onChange={this.handleInputChange('charge_code')}
              />
            </div>
            {this.renderIconPicker()}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label htmlFor="shipment-type">{t('SHIPMENT_TYPE')}</Label>
              <Select
                value={shipment_type}
                onValueChange={this.handleSelectChange('shipment_type')}
              >
                <SelectTrigger id="shipment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shipmentTypes.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {t(item.localizationSlug)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="pt-1">
                {this.renderToggleRow(
                  'admin_only',
                  'admin_only',
                  'ADMIN_ONLY_SELECT',
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="unit-type">{t('UNIT_TYPE')}</Label>
              <Select
                value={unit_type}
                onValueChange={this.handleSelectChange('unit_type')}
              >
                <SelectTrigger id="unit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {unitTypes.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {t(item.localizationSlug)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rate-type">{t('RATE_TYPE')}</Label>
              <Select
                value={rate_type}
                onValueChange={this.handleSelectChange('rate_type')}
              >
                <SelectTrigger id="rate-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rateTypes.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {t(item.localizationSlug)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isDrayage && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="anchor-event">{t('ANCHOR_DATE')}</Label>
                <Select
                  value={anchor_event ?? DELIVERY_STOP}
                  onValueChange={this.handleSelectChange('anchor_event')}
                  disabled={!hasFinanceAdminRole}
                >
                  <SelectTrigger id="anchor-event">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anchorEventOptions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {t(item.localizationSlug)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t('ANCHOR_DATE_HELP', 'Starts the request deadline.')}
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="accessorial-request-limit">
                  {t('ACCESSORIAL_REQUEST_LIMIT')}
                </Label>
                <Input
                  id="accessorial-request-limit"
                  type="number"
                  min="0"
                  value={days_before_auto_decline ?? ''}
                  onChange={this.handleInputChange(
                    'days_before_auto_decline',
                    'number',
                  )}
                  disabled={!hasFinanceAdminRole}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'ACCESSORIAL_REQUEST_LIMIT_HELP',
                    'Business days allowed before missing documentation may auto-decline the request.',
                  )}
                </p>
              </div>
            </div>
          )}

          <div>
            <Label className="block mb-2">{t('REQUIRED_FIELDS')}</Label>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
              {this.renderToggleRow(
                'begin_date_required',
                'begin_date_required',
                'BEGIN_DATE',
              )}
              {this.renderToggleRow(
                'end_date_required',
                'end_date_required',
                'END_DATE',
              )}
              {this.renderToggleRow(
                'location_required',
                'location_required',
                'LOCATION',
              )}
              {this.renderToggleRow(
                'description_required',
                'description_required',
                'DESCRIPTION',
              )}
            </div>
          </div>

          <UnitCalculationSection
            accessorial={this.state.accessorial}
            onSwitchChange={this.handleSwitchChange}
            onSelectChange={this.handleSelectChange}
            onInputChange={this.handleInputChange}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button
            variant="outline"
            disabled={this.state.saving}
            onClick={this.props.onCancel}
          >
            {t('CANCEL')}
          </Button>
          <Button
            type="button"
            onClick={this.handleSave}
            disabled={this.state.saving}
          >
            {t('SAVE')}
          </Button>
        </div>
      </div>
    );
  }
}

AccessorialForm.propTypes = {
  accessorial: PropTypes.shape({
    name: PropTypes.string,
    shipment_type: PropTypes.oneOf(['drayage', 'shorthaul']),
    begin_date_required: PropTypes.bool,
    end_date_required: PropTypes.bool,
    location_required: PropTypes.bool,
    description_required: PropTypes.bool,
    rate_type: PropTypes.oneOf(['flat', 'hourly', 'daily']),
    unit_type: PropTypes.oneOf(['decimal', 'number']),
    admin_only: PropTypes.bool,
    auto_calculate_units: PropTypes.bool,
    calc_method: PropTypes.string,
    applies_to: PropTypes.string,
    decimal_places: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minimum_units: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onCancel: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
};

export default withTranslation()(AccessorialForm);
